import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Mail } from "lucide-react";
import { DISCIPLINES as DISC_PAL } from "@/lib/disciplines";

// Words that rotate in the hero — plays on "what kind of Artizen are you?"
// Final line lands on "You're an Artizen." for the punchline.
const ROTATE = [
  { prefix: "You're",  word: "a musician." },
  { prefix: "You're",  word: "a dancer." },
  { prefix: "You're",  word: "a filmmaker." },
  { prefix: "You're",  word: "a comedian." },
  { prefix: "You're",  word: "a writer." },
  { prefix: "You're",  word: "a designer." },
  { prefix: "You're",  word: "a director." },
  { prefix: "You're",  word: "a maker." },
  { prefix: "You're",  word: "a fashion mind." },
  { prefix: "You're",  word: "a theatre kid." },
  { prefix: "You're",  word: "a visual artist." },
  { prefix: "You're",  word: "a manager." },
  { prefix: "You're",  word: "an Artizen." },
];

const ROLE = [
  { k: "artist",  label: "I MAKE STUFF",                 sub: "Music · Film · Art · Words · Movement · Design" },
  { k: "brand",   label: "I HIRE PEOPLE WHO MAKE STUFF", sub: "Brands · Labels · Studios · Agencies" },
  { k: "curious", label: "JUST CURIOUS",                 sub: "Here to watch the thing get built" },
];

// Disciplines in palette order — used for the grid + "manager" + "everything" tile + "brand" tile
const MANAGER_TILE = { key: "manager", label: "Manager / Agent", hex: "#6B7B8A" };
const ALL_TILES = [
  ...DISC_PAL,
  MANAGER_TILE,
  { key: "multi", label: "A bit of everything", hex: "#9CA3AF" },
  { key: "brand", label: "I'm a brand", hex: "#F25C50" },
];

// Cheeky prompts — picked at random per session. All angle at "tell us what would actually help you".
const PROMPTS = [
  "What's the one thing that would actually help you right now?",
  "If anArtizen could build one thing for you, what would it be?",
  "What's the missing piece in your work week?",
  "Where do you keep hitting a wall as a creative in India?",
  "What would make your career feel a little less lonely?",
];

export default function ComingSoon() {
  const [form, setForm] = useState({ name: "", email: "", city: "", discipline: "", wish: "", role: "artist" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [stats, setStats] = useState({ total: 0, wishes: [], cities: [] });
  const [rotIdx, setRotIdx] = useState(0);
  const prompt = useMemo(() => PROMPTS[Math.floor(Math.random() * PROMPTS.length)], []);
  const formRef = useRef(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/signups/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/site/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  // Rotate the hero word every 1.6s (no longer used in JSX — hero now spotlights UA)
  // Kept as a no-op to avoid ripping out ROTATE/rotIdx state during the restructure.
  useEffect(() => {}, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.email) { toast.error("We need at least a name + email"); return; }
    if (!form.discipline) { toast.error("Pick what you make — or 'I'm nosy'"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/signups", form);
      setDone(true);
      if (data.duplicate) toast.message("You're already counted.");
      else toast.success("You've been seen. AZ-ID issued.");
      const s = await api.get("/signups/stats");
      setStats(s.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Marquee of recent wishes — answers only, no names or cities
  const marqueeWishes = stats.wishes?.length
    ? [...stats.wishes, ...stats.wishes].map((w) => `"${w.wish}"`)
    : null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col overflow-x-hidden">
      {/* Top bar */}
      <header className="border-b border-border z-30 relative">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-20 md:h-24 flex items-center justify-between gap-3">
          <img
            src="/anartizen-logo.png"
            alt="anArtizen"
            data-testid="header-logo"
            className="h-20 md:h-28 w-auto select-none -my-2"
            draggable={false}
          />
          <p className="meta-mono !text-white/60 hidden sm:block" data-testid="header-tagline">
            — THE DOOR IS NOW <span className="text-primary">OPEN</span>
          </p>
        </div>
      </header>

      {/* Hero — Unfair Advantage spotlight */}
      <section className="relative grid-bg border-b border-border">
        {/* Floating discipline color halos */}
        <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
          {DISC_PAL.map((d, i) => (
            <span
              key={d.key}
              className="absolute rounded-full opacity-[0.07] blur-2xl"
              style={{
                background: d.hex,
                width: 280 + (i % 3) * 80,
                height: 280 + (i % 3) * 80,
                left: `${(i * 13 + 7) % 90}%`,
                top: `${(i * 23 + 11) % 80}%`,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-6 md:pt-10 pb-12 md:pb-14 w-full z-10">
          <p className="label-mono mb-4" data-testid="hero-ua-eyebrow">
            <Sparkles size={11} /> UNFAIR ADVANTAGE · NOW LIVE
          </p>

          <h1 className="font-display font-bold leading-[0.98] md:leading-[0.95] tracking-tight text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl xl:text-[4.5rem] max-w-[1100px]">
            Solve for your <span className="hl-coral">creative journey.</span><br />
            Access the <span className="hl-cyan">best minds</span> in the room.
          </h1>

          <p className="mt-5 max-w-2xl text-white/85 text-sm md:text-base leading-relaxed">
            30-minute one-on-ones with the label bosses, producers, choreographers, editors and publishers actually reshaping India&apos;s creative industries. No gatekeeping. No pitch decks. Just the questions you&apos;ve been carrying around for years, answered by someone who can answer them.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Link
              to="/unfair-advantage"
              data-testid="hero-ua-cta"
              className="btn-pill btn-coral text-base px-6 py-3"
            >
              Book a session <ArrowRight size={14} />
            </Link>
            <p className="meta-mono !text-white/55">
              — HAVE A CODE? <span className="text-primary">·</span> APPLY IT AT CHECKOUT
            </p>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-px bg-border max-w-md" data-testid="hero-stats">
            <div className="bg-background p-3 md:p-4">
              <p className="num-display text-2xl md:text-4xl font-bold text-primary leading-none" data-testid="counter-artizens">{(stats.total || 0).toLocaleString("en-IN")}</p>
              <p className="meta-mono mt-2">{stats.total === 1 ? "ARTIZEN" : "ARTIZENS"}</p>
            </div>
            <div className="bg-background p-3 md:p-4">
              <p className="num-display text-2xl md:text-4xl font-bold text-secondary leading-none">{stats.cities?.length || 0}</p>
              <p className="meta-mono mt-2">CITIES</p>
            </div>
            <div className="bg-background p-3 md:p-4">
              <p className="num-display text-2xl md:text-4xl font-bold text-cream leading-none">9</p>
              <p className="meta-mono mt-2">CRAFTS</p>
            </div>
          </div>
        </div>
      </section>

      {/* TWO DOORS — Learn · Build (Grow is now the hero spotlight above) */}
      <section className="border-t border-border" data-testid="home-two-doors">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <p className="label-mono mb-6">
            <Sparkles size={11} /> What&apos;s also open · Two more doors, no gatekeeping
          </p>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-end mb-14">
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold leading-[0.9] tracking-tight">
              Two more doors. <span className="font-serif-italic font-normal text-white/70">Walk in.</span>
            </h2>
            <p className="text-white/75 text-base md:text-lg leading-relaxed">
              Beyond the sessions, there&apos;s more you can already use — one to <span className="hl-cyan">learn</span>, one to <span className="hl-cream">build</span>. Pick a door and go.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-border">
            <DoorCard
              num="01"
              label="Learn"
              product="No Pressure"
              color="#7EE7DC"
              body="Loud, honest conversations with India's working creatives. Musicians, filmmakers, comedians, designers — saying the things they don't say on stage. New episodes drop monthly."
              cta="Watch on YouTube"
              to="/no-pressure"
              testid="door-learn"
            />
            <DoorCard
              num="02"
              label="Build"
              product="The Build Room"
              color="#F5E7C6"
              body="Bring us what you're building — a campaign, a record, a book, an exhibition. We scope, we cost, we ship. For brands, labels and institutions — and for artists on specific projects. Paid engagements only."
              cta="Send us a brief"
              to="/build"
              testid="door-build"
            />
          </div>
        </div>
      </section>

      {/* REGISTER YOUR PRESENCE — the form, moved down from the hero */}
      <section id="register" className="border-t border-border" data-testid="home-register-block" ref={formRef}>
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-end mb-10">
            <div>
              <p className="label-mono mb-4">
                <Sparkles size={11} /> Not ready to book yet? Register for what&apos;s next
              </p>
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-[0.92] tracking-tight">
                Get your <span className="hl-coral">Artizen ID.</span><br />
                <span className="text-white/80">Stay early.</span>
              </h2>
            </div>
            <p className="text-white/75 text-base md:text-lg leading-relaxed">
              Register your presence and we&apos;ll issue you an Artizen ID — the one thing that stays yours as the rest of it unlocks. Discovery, distribution, residencies, the pass. <span className="hl-cyan">You&apos;re early. That matters.</span>
            </p>
          </div>

          <div>
            {!done ? (
              <form onSubmit={submit} className="border border-border p-5 md:p-8 bg-background relative">
                <div className="absolute -top-3 left-5 px-2 bg-background">
                  <p className="meta-mono !text-primary">— REGISTER YOUR PRESENCE · 30 SECONDS · NO PASSWORD</p>
                </div>

                <div className="grid gap-px bg-border mt-2">
                  <F label="Your name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="cs-name" placeholder="What the world calls you" />
                  <F label="Email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} testid="cs-email" placeholder="we promise not to spam" />
                  <F label="Where do you make work?" v={form.city} on={(v) => setForm({ ...form, city: v })} testid="cs-city" placeholder="Bandra · Goa · Pondicherry" />

                  {/* Discipline grid — tactile palette */}
                  <div className="bg-background p-4">
                    <p className="meta-mono mb-3">— WHAT DO YOU MAKE?</p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                      {ALL_TILES.map((t) => {
                        const active = form.discipline === t.key;
                        return (
                          <button
                            key={t.key}
                            type="button"
                            data-testid={`cs-disc-${t.key}`}
                            onClick={() => setForm({ ...form, discipline: t.key, role: t.key === "brand" ? "brand" : form.role })}
                            className="border text-[10px] font-mono uppercase tracking-[0.16em] py-2 px-1.5 leading-tight transition"
                            style={{
                              borderColor: active ? t.hex : "rgba(255,255,255,0.12)",
                              background: active ? t.hex : "transparent",
                              color: active ? "#0a0a0a" : "rgba(255,255,255,0.85)",
                            }}
                          >
                            {t.label.toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Role */}
                  <div className="bg-background p-4">
                    <p className="meta-mono mb-3">— I AM…</p>
                    <div className="grid sm:grid-cols-3 gap-1.5">
                      {ROLE.map((r) => (
                        <button
                          key={r.k}
                          type="button"
                          data-testid={`cs-role-${r.k}`}
                          onClick={() => setForm({ ...form, role: r.k })}
                          className={`text-left p-3 border text-[10px] font-mono uppercase tracking-[0.16em] transition ${
                            form.role === r.k ? "border-primary bg-primary/10" : "border-border hover:border-white/40"
                          }`}
                        >
                          <p className={form.role === r.k ? "text-primary" : "text-white"}>{r.label}</p>
                          <p className="meta-mono mt-1 normal-case tracking-[0.04em] !text-white/55 text-[10px]">{r.sub}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Cheeky wish */}
                  <div className="bg-background p-4">
                    <p className="meta-mono mb-2">— {prompt.toUpperCase()}</p>
                    <textarea
                      data-testid="cs-wish"
                      rows={3}
                      value={form.wish}
                      onChange={(e) => setForm({ ...form, wish: e.target.value })}
                      placeholder="Optional. Honest. Funny. Both."
                      className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-base font-light leading-relaxed"
                      maxLength={240}
                    />
                    <p className="meta-mono mt-2 !text-white/40 text-right">{form.wish.length}/240</p>
                  </div>
                </div>

                <button data-testid="cs-submit" disabled={submitting} className="btn-pill btn-coral w-full justify-center mt-5">
                  {submitting ? "Counting you in…" : <>Count me in — issue my Artizen ID <ArrowRight size={14} /></>}
                </button>
                <p className="meta-mono mt-3 !text-white/40 text-center">Your ID is yours. Everything else unlocks over time.</p>
              </form>
            ) : (
              <SuccessCard
                pin={stats.total || 1}
                name={form.name}
                discipline={form.discipline}
                reset={() => { setDone(false); setForm({ name: "", email: "", city: "", discipline: "", wish: "", role: "artist" }); }}
              />
            )}
          </div>

          {/* Newsletter — moved down with the form */}
          {settings?.newsletter_enabled !== false && (
            <div className="mt-14 max-w-2xl">
              <NewsletterBlock blurb={settings?.newsletter_blurb} />
            </div>
          )}
        </div>
      </section>

      {/* Wishes marquee */}
      {marqueeWishes && (
        <section className="border-t border-border py-6 overflow-hidden bg-[#0c0c0e]">
          <p className="meta-mono px-5 md:px-10 mb-3">— WHAT THIS COMMUNITY IS SAYING · LIVE FROM THE FORM</p>
          <div className="marquee whitespace-nowrap">
            {marqueeWishes.map((w, i) => (
              <span key={i} className="font-serif-italic text-white/80 text-lg md:text-2xl shrink-0 inline-flex items-center gap-6">
                {w}
                <span className="text-primary">●</span>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* WHAT'S COMING — teaser strip */}
      <section className="border-t border-border" data-testid="home-coming-soon">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16 md:py-20">
          <div className="grid md:grid-cols-[1fr_auto] gap-8 items-end mb-8">
            <div>
              <p className="label-mono mb-4">— Coming next · For those here early</p>
              <h2 className="font-display text-3xl md:text-5xl font-bold leading-[0.95] tracking-tight">
                More doors. <span className="hl-coral">Being built.</span>
              </h2>
            </div>
            <p className="text-white/70 max-w-md text-sm md:text-base leading-relaxed">
              Register your presence above and you&apos;ll be the first through when these open.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {[
              ["Discovery",     "A verified directory of India's creatives. Findable. Bookable."],
              ["Opportunity",   "Live briefs from brands, labels, and studios. Apply in a click."],
              ["Collaborations","Artist-to-artist introductions. Cross-discipline. Curated."],
              ["Resources",     "Subsidised studios, gear and spaces. Because rent is theft of time."],
            ].map(([k, v]) => (
              <div key={k} className="bg-background p-6 md:p-7" data-testid={`coming-${k.toLowerCase()}`}>
                <p className="meta-mono !text-white/45">— SOON</p>
                <p className="font-display text-2xl md:text-3xl font-bold mt-3 leading-tight tracking-tight">{k}</p>
                <p className="text-white/65 text-sm mt-3 leading-relaxed">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-6 flex flex-wrap items-center justify-between gap-3">
          <p className="meta-mono">Made in India · for the people who make culture here</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Link to="/privacy" className="meta-mono hover:!text-white transition" data-testid="landing-footer-privacy">Privacy</Link>
            <Link to="/terms" className="meta-mono hover:!text-white transition" data-testid="landing-footer-terms">Terms</Link>
            <Link to="/refunds" className="meta-mono hover:!text-white transition" data-testid="landing-footer-refunds">Refunds</Link>
            <Link to="/contact" className="meta-mono hover:!text-white transition" data-testid="landing-footer-contact">Contact</Link>
            <span className="meta-mono">© 2026 · anArtizen</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function DoorCard({ num, label, product, color, body, cta, to, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group bg-background p-8 md:p-10 flex flex-col min-h-[420px] hover:bg-white/[0.02] transition"
    >
      <div className="flex items-center justify-between">
        <p className="meta-mono">— {num}</p>
        <span
          aria-hidden
          className="block w-2.5 h-2.5 rounded-full"
          style={{ background: color }}
        />
      </div>
      <h3
        className="font-display text-6xl md:text-7xl font-bold leading-none mt-6 mb-3"
        style={{ color }}
      >
        {label}.
      </h3>
      <p className="meta-mono !text-white/55 mb-6">— {product.toUpperCase()}</p>
      <p className="text-white/80 leading-relaxed">{body}</p>
      <div className="mt-auto pt-8 border-t border-border flex items-center justify-between">
        <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color }}>{cta}</span>
        <ArrowRight
          size={18}
          className="opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition"
          style={{ color }}
        />
      </div>
    </Link>
  );
}

function SuccessCard({ pin, name, discipline, reset }) {
  const palette = DISC_PAL.find((d) => d.key === discipline);
  const color = palette?.hex || "#F25C50";
  const idCode = `AZ-${String(pin).padStart(5, "0")}`;
  const COMING = [
    { k: "discovery",     label: "Discovery" },
    { k: "opportunity",   label: "Opportunity" },
    { k: "collaborations",label: "Collaborations" },
    { k: "resources",     label: "Resources" },
  ];
  return (
    <div className="relative border bg-background p-6 md:p-8 reveal-up" style={{ borderColor: color }} data-testid="cs-success">
      <p className="label-mono mb-4" style={{ color }}><Sparkles size={11} /> YOU&apos;VE BEEN SEEN</p>
      <h2 className="font-display text-4xl md:text-5xl font-bold leading-[0.95]">
        Welcome, <br /><span style={{ color }}>{name?.split(" ")[0] || "Artizen"}.</span>
      </h2>
      <div className="mt-8 border-t border-b border-border py-5">
        <p className="meta-mono">— YOUR ARTIZEN ID</p>
        <p className="num-display text-4xl md:text-5xl font-bold mt-2 tracking-tight" style={{ color }}>{idCode}</p>
        <p className="meta-mono mt-3 !text-white/55">— YOURS. PERMANENT. USE IT EVERY TIME.</p>
      </div>
      <p className="mt-6 text-white/80 leading-relaxed">
        Three doors are already open — <strong>Grow, Learn, Build</strong>. Everything else is on its way.
      </p>
      <div className="mt-5" data-testid="cs-coming-soon">
        <p className="meta-mono mb-3 !text-white/55">— COMING SOON, FOR THOSE HERE EARLY</p>
        <div className="grid grid-cols-2 gap-1.5">
          {COMING.map((c) => (
            <span
              key={c.k}
              data-testid={`cs-benefit-${c.k}`}
              className="border border-border px-3 py-2 font-mono text-[10px] tracking-[0.18em] uppercase text-white/85"
            >
              {c.label}
            </span>
          ))}
        </div>
      </div>
      <button onClick={reset} className="btn-pill mt-6" data-testid="cs-reset">Register someone else</button>
    </div>
  );
}

function F({ label, v, on, type = "text", testid, placeholder }) {
  return (
    <div className="bg-background p-4">
      <p className="meta-mono mb-2">— {label.toUpperCase()}</p>
      <input
        data-testid={testid}
        type={type}
        value={v}
        placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
        className="w-full bg-transparent outline-none border-b border-border focus:border-primary py-2 text-base md:text-lg font-light"
      />
    </div>
  );
}

// ============= NEWSLETTER BLOCK =============
function NewsletterBlock({ blurb }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("We need an email"); return; }
    setSending(true);
    try {
      await api.post("/newsletter/subscribe", { email, name, source: "landing" });
      setDone(true);
      toast.success("On the list.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not subscribe");
    } finally { setSending(false); }
  };

  return (
    <div className="mt-10 border border-border bg-background p-5 md:p-6" data-testid="landing-newsletter">
      <p className="meta-mono !text-cream"><Mail size={11} /> THE NEWSLETTER · IYKYK</p>
      <p className="font-display text-2xl md:text-3xl font-bold mt-2 leading-tight">
        {done ? <>You&apos;re <span className="hl-cyan">on the list.</span></> : <>One email a month. <span className="hl-cream">No spam.</span></>}
      </p>
      <p className="text-white/70 text-sm mt-3 leading-relaxed">{blurb || "A monthly dispatch on Indian creative culture. No spam. Honest stuff."}</p>
      {!done && (
        <form onSubmit={subscribe} className="mt-5 grid sm:grid-cols-[1fr_auto] gap-2">
          <div className="grid sm:grid-cols-2 gap-2 sm:col-span-2">
            <input
              data-testid="newsletter-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional)"
              className="bg-transparent border border-border px-3 py-2.5 outline-none focus:border-primary text-sm"
            />
            <input
              data-testid="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.in"
              className="bg-transparent border border-border px-3 py-2.5 outline-none focus:border-primary text-sm"
            />
          </div>
          <button data-testid="newsletter-submit" disabled={sending} className="btn-pill btn-coral sm:col-span-2 justify-center mt-1">
            {sending ? "Subscribing…" : <>Subscribe <ArrowRight size={14} /></>}
          </button>
        </form>
      )}
    </div>
  );
}
